/**
 * The line above the report (12 Oct): "Data pulled HH:MM ET · next update
 * HH:MM ET (HH:MM Denmark)", and Rusty's "Update now" button.
 *
 * The next update is the next real slot from the server (07:00 Denmark,
 * 07:00 and 12:00 US Eastern). "Update now" is rendered only when the server
 * says this reader may use it (`can_update_now`: Rusty alone); for anyone
 * else it is not in the page at all, and the server refuses them (403).
 */

import { Icon, RefreshCw } from "@norican/icons";
import { Button, Typography } from "@norican/react";

import { useLanguage, useT } from "@/i18n";

import { useReportRefreshStatus, useUpdateReportNow } from "./api";
import {
  clockTime,
  HELPDESK_OVERVIEW,
  nextUpdateTimes,
  pulledTime,
  showUpdateNow,
  updateNowReady,
} from "./report-helpers";

export function RefreshLine({
  pulledAt,
  extra = [],
}: {
  pulledAt: string | null | undefined;
  extra?: (string | null)[];
}) {
  const { t } = useT("common");
  const { locale } = useLanguage();
  const status = useReportRefreshStatus(HELPDESK_OVERVIEW);
  const pulled = pulledTime(pulledAt ?? status.data?.last_success_at, locale);
  const next = nextUpdateTimes(status.data?.next_run_at, locale);
  const off = status.data?.mode === "off";

  return (
    <Typography variant="caption" tone="muted">
      {[
        pulled ? t("leadershipReport.status.pulled", { time: pulled }) : null,
        status.data?.running ? t("leadershipReport.status.running") : null,
        off
          ? t("leadershipReport.status.off")
          : next
            ? t("leadershipReport.status.nextUpdate", { et: next.et, dk: next.dk })
            : null,
        ...extra,
      ]
        .filter(Boolean)
        .join(" · ")}
    </Typography>
  );
}

/** Rusty's "Update now". Returns nothing at all for anyone else. */
export function UpdateNowButton() {
  const { t } = useT("common");
  const { locale } = useLanguage();
  const status = useReportRefreshStatus(HELPDESK_OVERVIEW);
  const update = useUpdateReportNow(HELPDESK_OVERVIEW);
  if (!showUpdateNow(status.data)) {
    return null;
  }
  const ready = updateNowReady(status.data) && !update.isPending;
  const waitUntil = clockTime(status.data?.update_now_available_at, "America/New_York", locale);
  const title = status.data?.running
    ? t("leadershipReport.updateNow.running")
    : !ready && waitUntil
      ? t("leadershipReport.updateNow.wait", { time: `${waitUntil} ET` })
      : t("leadershipReport.updateNow.hint");
  return (
    <Button
      size="sm"
      variant="outline"
      tone="neutral"
      startIcon={<Icon icon={RefreshCw} size="sm" />}
      disabled={!ready}
      title={title}
      onClick={() => update.mutate()}
    >
      {status.data?.running || update.isPending
        ? t("leadershipReport.updateNow.running")
        : t("leadershipReport.updateNow.label")}
    </Button>
  );
}
